import React, { useState, useEffect } from 'react';
import CheckInHistoryModal from './CheckInHistoryModal';
import { extractPinFromContact, extractLastCheckinFromContact, extractDateJoinedFromContact, formatContactForDisplay } from '../utils/contactUtils';

const TFUNDashboard = ({ locationId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCheckInUrl, setShowCheckInUrl] = useState(false);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [showManualCheckIn, setShowManualCheckIn] = useState(false);
    const [manualCheckInLoading, setManualCheckInLoading] = useState(false);
    const [activeView, setActiveView] = useState('dashboard');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [checkinStats, setCheckinStats] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [showCheckInHistory, setShowCheckInHistory] = useState(false);
    const [selectedMemberForHistory, setSelectedMemberForHistory] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);
    const [todaysCheckins, setTodaysCheckins] = useState([]);
    const [todaysCheckinsDate, setTodaysCheckinsDate] = useState('');
    const [locationTimezone, setLocationTimezone] = useState('UTC');
    const [allCheckins, setAllCheckins] = useState([]);
    const [checkinPagination, setCheckinPagination] = useState({
        page: 1,
        limit: 20,
        total_count: 0,
        total_pages: 0
    });
    const [checkinFilters, setCheckinFilters] = useState({
        search: '',
        dateFrom: '',
        dateTo: ''
    });
    const [filters, setFilters] = useState({
        dateFrom: '',
        dateTo: '',
        beltRank: ''
    });
    const [hasActiveFilters, setHasActiveFilters] = useState(false);

    // TFUN Color Scheme
    const colors = {
        primary: '#1e40af', 
        primaryDark: '#1e3a8a',
        secondary: '#f8fafc',
        accent: '#3b82f6',
        text: '#0f172a',
        textLight: '#64748b',
        success: '#10b981',
        warning: '#f59e0b',
        danger: '#ef4444',
        border: '#e2e8f0',
        card: '#ffffff',
        sidebar: '#1e293b'
    };

    // Get current time in location timezone
    const getCurrentTimeInTimezone = (timezone) => {
        const now = new Date();
        const timeStr = now.toLocaleTimeString('en-US', {
            timeZone: timezone,
            hour12: false,
            hour: '2-digit',
            minute: '2-digit'
        });
        return timeStr;
    };

    // Load location timezone
    const loadLocationTimezone = async () => {
        try {
            console.log('📡 Calling location-details API...');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ghl/location-details/${locationId}`);
            console.log('📡 Location-details response status:', response.status);
            
            if (response.ok) {
                const result = await response.json();
                const timezone = result.timezone || 'UTC';
                console.log('✅ Location timezone loaded:', timezone);
                setLocationTimezone(timezone);
                return timezone;
            } else {
                console.warn('⚠️ Could not load location timezone, defaulting to UTC. Status:', response.status);
                setLocationTimezone('UTC');
                return 'UTC';
            }
        } catch (err) {
            console.error('❌ Failed to load location timezone:', err);
            setLocationTimezone('UTC');
            return 'UTC';
        }
    };

    // Get current date in location timezone
    const getCurrentDateInTimezone = (timezone) => {
        const now = new Date();
        const options = { 
            timeZone: timezone, 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit' 
        };
        const dateStr = now.toLocaleDateString('en-CA', options); // YYYY-MM-DD format
        return dateStr;
    };

    // Format timestamp to location timezone (always interpret stored timestamp as UTC)
    const formatTimeInTimezone = (timestamp, timezone) => {
        const date = new Date(timestamp + 'Z'); // Force interpret as UTC by adding Z
        return date.toLocaleTimeString('en-US', {
            timeZone: timezone,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    // Load all check-ins with pagination
    const loadAllCheckins = async (page = 1, limit = 20, search = '', dateFrom = '', dateTo = '') => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkin/recent/${locationId}?limit=100`);
            if (response.ok) {
                const result = await response.json();
                let filtered = result.checkins || [];
                
                // Add check-in type
                filtered = filtered.map(checkin => ({
                    ...checkin,
                    checkin_type: checkin.device_info === "Manual Check-in" ? "Manual" : "Kiosk"
                }));
                
                // Apply date filters
                if (dateFrom) {
                    filtered = filtered.filter(checkin => checkin.checkin_date >= dateFrom);
                }
                if (dateTo) {
                    filtered = filtered.filter(checkin => checkin.checkin_date <= dateTo);
                }
                
                // Apply search filter
                if (search) {
                    filtered = filtered.filter(checkin => {
                        const name = checkin.contact_name || '';
                        const pin = checkin.pin_used || '';
                        const rank = checkin.belt_rank || '';
                        return name.toLowerCase().includes(search.toLowerCase()) ||
                               pin.toLowerCase().includes(search.toLowerCase()) ||
                               rank.toLowerCase().includes(search.toLowerCase());
                    });
                }
                
                // Sort by checkin_timestamp in descending order (most recent first)
                // This ensures manual backdated check-ins appear in correct chronological order
                filtered.sort((a, b) => {
                    const dateA = new Date(a.checkin_timestamp);
                    const dateB = new Date(b.checkin_timestamp);
                    return dateB - dateA; // Descending order (newest first)
                });
                
                // Calculate pagination
                const startIndex = (page - 1) * limit;
                const endIndex = startIndex + limit;
                const paginatedCheckins = filtered.slice(startIndex, endIndex);
                
                setAllCheckins(paginatedCheckins);
                setCheckinPagination({
                    page,
                    limit,
                    total_count: filtered.length,
                    total_pages: Math.ceil(filtered.length / limit),
                    has_next: endIndex < filtered.length,
                    has_prev: page > 1
                });
                
                console.log('All check-ins loaded:', {
                    totalRecords: result.checkins?.length || 0,
                    filteredRecords: filtered.length,
                    page,
                    limit,
                    displayedRecords: paginatedCheckins.length
                });
            }
        } catch (err) {
            console.error('Failed to load all check-ins:', err);
        }
    };

    // Load today's check-ins
    const loadTodaysCheckins = async (timezone = locationTimezone) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkin/recent/${locationId}?limit=100`);
            if (response.ok) {
                const result = await response.json();
                
                // Get "today" in the location's timezone
                const today = getCurrentDateInTimezone(timezone);
                
                // First try to get check-ins for actual today (in location timezone)
                let todaysOnly = (result.checkins || []).filter(checkin => {
                    return checkin.checkin_date === today;
                });
                
                let displayDate = today;
                
                // If no check-ins today, get the most recent day with check-ins
                if (todaysOnly.length === 0 && result.checkins && result.checkins.length > 0) {
                    // Find the most recent checkin_date
                    const sortedDates = [...new Set(result.checkins.map(c => c.checkin_date))].sort().reverse();
                    const mostRecentDate = sortedDates[0];
                    displayDate = mostRecentDate;
                    
                    todaysOnly = result.checkins.filter(checkin => {
                        return checkin.checkin_date === mostRecentDate;
                    });
                }
                
                // Sort today's check-ins by checkin_timestamp in descending order
                todaysOnly.sort((a, b) => {
                    const dateA = new Date(a.checkin_timestamp);
                    const dateB = new Date(b.checkin_timestamp);
                    return dateB - dateA; // Descending order (newest first)
                });
                
                setTodaysCheckins(todaysOnly);
                setTodaysCheckinsDate(displayDate);
            }
        } catch (err) {
            console.error('Failed to load today\'s check-ins:', err);
        }
    };

    // Load location data
    const loadLocationData = async () => {
        try {
            console.log('📡 Starting loadLocationData...');
            setLoading(true);
            
            // Load location timezone first (automatic token generation is handled in backend)
            console.log('📡 Loading location timezone...');
            const timezone = await loadLocationTimezone();
            console.log('✅ Location timezone loaded:', timezone);
            
            console.log('📡 Loading contacts...');
            
            // Use the fixed main backend contacts endpoint
            try {
                console.log('📡 Calling main contacts endpoint with fixed pagination...');
                
                const response = await Promise.race([
                    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ghl/contacts/${locationId}`),
                    new Promise((_, reject) => 
                        setTimeout(() => reject(new Error('Timeout after 60 seconds')), 60000)
                    )
                ]);
                
                console.log('📡 Main endpoint response status:', response.status);
                
                if (!response.ok) {
                    if (response.status === 401) {
                        console.log('🔄 Authentication failed, redirecting to OAuth...');
                        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/authorize-agency`;
                        return;
                    }
                    const errorText = await response.text();
                    throw new Error(`Backend API failed: ${response.status} - ${errorText}`);
                }
                
                const result = await response.json();
                console.log('✅ Fixed pagination results:');
                console.log(`📊 Total contacts fetched: ${result.all_contacts} (expected: 931)`);
                console.log(`🎯 Active members found: ${result.total} (expected: 47)`);
                console.log(`📄 Pages fetched: ${result.pages_fetched}`);
                console.log(`🔧 GHL API fixed: ${result.ghl_api_fixed}`);
                
                // Look for Chris Emerson specifically
                const chrisEmerson = result.contacts.find(c => c.id === 'BJrPEJ49rMh2Zsa02xAl');
                if (chrisEmerson) {
                    console.log('✅ Found Chris Emerson:', chrisEmerson.contactName, chrisEmerson.tags);
                } else {
                    console.log('❌ Chris Emerson still not found');
                }
                
                // Log sample contacts for debugging
                if (result.sample_contacts && result.sample_contacts.length > 0) {
                    console.log('📋 Sample contacts from backend:', result.sample_contacts);
                }
                
                setData(result);
                
            } catch (contactsError) {
                console.error('❌ Fixed contacts API failed:', contactsError.message);
                const fallbackData = {
                    contacts: [],
                    total: 0,
                    all_contacts: 0,
                    location_id: locationId,
                    error: true,
                    error_message: contactsError.message
                };
                console.log('🔄 Using fallback data for contacts');
                setData(fallbackData);
            }
            
            // Reload today's check-ins with the correct timezone
            console.log('📡 Loading todays checkins...');
            loadTodaysCheckins(timezone);
            
            // Load check-in stats with timeout
            console.log('📡 Loading checkin stats...');
            try {
                const statsTimeoutPromise = new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Stats API timeout')), 5000)
                );
                const statsResponse = await Promise.race([
                    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkin/stats/${locationId}`),
                    statsTimeoutPromise
                ]);
                
                if (statsResponse.ok) {
                    const stats = await statsResponse.json();
                    console.log('✅ Stats loaded:', stats);
                    setCheckinStats(stats);
                } else {
                    console.warn('⚠️ Stats failed:', statsResponse.status);
                }
            } catch (statsError) {
                console.warn('⚠️ Stats API failed:', statsError.message);
                // Continue without stats
            }
            
            // Load new members count from API with timeout
            console.log('📡 About to load new members count...');
            try {
                await Promise.race([
                    loadNewMembersCount(),
                    new Promise((_, reject) => 
                        setTimeout(() => reject(new Error('New members API timeout')), 5000)
                    )
                ]);
                console.log('✅ Finished loading new members count');
            } catch (newMembersError) {
                console.warn('⚠️ New members count failed:', newMembersError.message);
                // Continue without new members count
            }
        } catch (err) {
            console.error('❌ Error in loadLocationData:', err);
            setError(err.message);
        } finally {
            console.log('🏁 loadLocationData finished');
            setLoading(false);
        }
    };

    useEffect(() => {
        if (locationId) {
            loadLocationData();
        }
    }, [locationId]);

    // Apply search and filters whenever they change
    useEffect(() => {
        applyFilters();
    }, [searchTerm, filters, data]);

    // Utility function to capitalize names
    const capitalizeName = (name) => {
        if (!name) return 'Unknown';
        return name.split(' ').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
    };

    // Get member picture
    const getMemberPicture = (member) => {
        const pictureField = member.customFields?.find(f => 
            f.key === 'contact.member_picture' || f.fieldKey === 'contact.member_picture'
        );
        return pictureField?.value || pictureField?.field_value || null;
    };

    // Get last check-in date using scalable approach
    const getLastCheckInDate = (member) => {
        const lastCheckinDate = extractLastCheckinFromContact(member);
        
        if (lastCheckinDate && lastCheckinDate !== 'Never') {
            let parsedDate;
            if (typeof lastCheckinDate === 'number') {
                const utcDate = new Date(lastCheckinDate);
                parsedDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
            } else if (typeof lastCheckinDate === 'string') {
                parsedDate = new Date(lastCheckinDate);
            } else {
                return 'Never';
            }
            
            return isNaN(parsedDate.getTime()) ? 'Never' : parsedDate;
        }
        
        return 'Never';
    };

    // Get member join date
    const getMemberJoinDate = (member) => {
        // Use the scalable utility function for date joined
        const dateJoined = extractDateJoinedFromContact(member);
        
        if (dateJoined && dateJoined !== 'Unknown') {
            let parsedDate;
            
            if (typeof dateJoined === 'number') {
                // Handle timestamp in milliseconds
                parsedDate = new Date(dateJoined);
            } else if (typeof dateJoined === 'string') {
                // Handle string dates - check if it's a timestamp string
                if (dateJoined.match(/^\d+$/)) {
                    parsedDate = new Date(parseInt(dateJoined));
                } else {
                    parsedDate = new Date(dateJoined);
                }
            } else {
                return null;
            }
            
            if (!isNaN(parsedDate.getTime())) {
                return parsedDate;
            }
        }
        
        return null;
    };

    // Calculate days since last check-in
    const getDaysSinceLastCheckIn = (lastCheckIn) => {
        if (!lastCheckIn || lastCheckIn === 'Never') return 'Never';
        
        // Ensure lastCheckIn is a Date object
        let checkInDate = lastCheckIn;
        if (typeof lastCheckIn === 'string') {
            checkInDate = new Date(lastCheckIn);
        }
        
        // Validate that we have a valid date
        if (isNaN(checkInDate.getTime())) {
            return 'Never';
        }
        
        const today = new Date();
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const checkInStart = new Date(checkInDate.getFullYear(), checkInDate.getMonth(), checkInDate.getDate());
        
        const diffTime = todayStart - checkInStart;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
            return 0;
        }
        
        return diffDays;
    };

    // State to store new members count from API
    const [newMembersCount, setNewMembersCount] = useState(0);
    // State to store detailed new members data from API
    const [newMembersData, setNewMembersData] = useState([]);

    // Function to load new members count from API
    const loadNewMembersCount = async () => {
        try {
            console.log('Loading new members count for location:', locationId);
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkin/new-members/${locationId}`);
            console.log('New members API response status:', response.status);
            
            if (response.ok) {
                const data = await response.json();
                console.log('New members API data:', data);
                setNewMembersCount(data.count || 0);
                // Store the detailed new members data for the report view
                setNewMembersData(data.new_members || []);
            } else {
                console.error('Failed to load new members count, status:', response.status);
                setNewMembersCount(0);
                setNewMembersData([]);
            }
        } catch (error) {
            console.error('Error loading new members count:', error);
            setNewMembersCount(0);
            setNewMembersData([]);
        }
    };

    // Get new members this month (now uses API data)
    const getNewMembersThisMonth = () => {
        return newMembersCount;
    };

    // Get total active members
    const getTotalActiveMembers = () => {
        return data?.contacts?.length || 0;
    };

    // Get members who checked in last 7 days (in location timezone)
    const getRecentCheckIns = () => {
        if (!data?.contacts) return 0;
        
        // Calculate 7 days ago in location timezone
        const now = new Date();
        const nowInTimezone = new Date(now.toLocaleString("en-US", {timeZone: locationTimezone}));
        const sevenDaysAgo = new Date(nowInTimezone);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        return data.contacts.filter(member => {
            const lastCheckIn = getLastCheckInDate(member);
            return lastCheckIn && lastCheckIn >= sevenDaysAgo;
        }).length;
    };

    // Get MIA members (7+ days without check-in, in location timezone)
    const getMIAMembers = () => {
        if (!data?.contacts) return 0;
        
        // Calculate 7 days ago in location timezone
        const now = new Date();
        const nowInTimezone = new Date(now.toLocaleString("en-US", {timeZone: locationTimezone}));
        const sevenDaysAgo = new Date(nowInTimezone);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        return data.contacts.filter(member => {
            const lastCheckIn = getLastCheckInDate(member);
            return !lastCheckIn || lastCheckIn < sevenDaysAgo;
        }).length;
    };

    // Get filtered data based on view type
    const getFilteredDataForView = (viewType) => {
        if (!data?.contacts) return [];
        
        let filtered = data.contacts;
        
        // Apply view-specific filters (using location timezone)
        switch (viewType) {
            case 'new-members':
                // Use API data instead of frontend filtering
                if (newMembersData && newMembersData.length > 0) {
                    // Convert API data to match the expected format
                    filtered = newMembersData.map(member => ({
                        id: member.id,
                        contactName: `${member.firstName} ${member.lastName}`.trim(),
                        firstName: member.firstName,
                        lastName: member.lastName,
                        email: member.email,
                        phone: member.phone,
                        tags: ['active member'], // Assume these are active members from the API
                        customFields: member.customFields || [],
                        // Add any other properties that might be needed
                        dateJoined: member.dateJoined,
                        joinedTimestamp: member.joinedTimestamp
                    }));
                } else {
                    filtered = []; // No new members data available
                }
                break;
            case 'recent-activity':
                const nowRecent = new Date();
                const nowRecentInTimezone = new Date(nowRecent.toLocaleString("en-US", {timeZone: locationTimezone}));
                const sevenDaysAgo = new Date(nowRecentInTimezone);
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                filtered = filtered.filter(member => {
                    const lastCheckIn = getLastCheckInDate(member);
                    return lastCheckIn && lastCheckIn >= sevenDaysAgo;
                });
                break;
            case 'mia-members':
                const nowMIA = new Date();
                const nowMIAInTimezone = new Date(nowMIA.toLocaleString("en-US", {timeZone: locationTimezone}));
                const sevenDaysAgoMIA = new Date(nowMIAInTimezone);
                sevenDaysAgoMIA.setDate(sevenDaysAgoMIA.getDate() - 7);
                filtered = filtered.filter(member => {
                    const lastCheckIn = getLastCheckInDate(member);
                    return !lastCheckIn || lastCheckIn < sevenDaysAgoMIA;
                });
                break;
            default: // active-members and others
                break;
        }
        
        // Apply search term
        if (searchTerm) {
            filtered = filtered.filter(member => {
                const name = member.contactName || '';
                const email = member.email || '';
                const phone = member.phone || '';
                return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       phone.toLowerCase().includes(searchTerm.toLowerCase());
            });
        }
        
        return filtered;
    };

    // Copy check-in URL to clipboard
    const copyCheckInUrl = () => {
        const checkInUrl = `${window.location.origin}/simple-checkin?location_id=${locationId}`;
        navigator.clipboard.writeText(checkInUrl).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };

    // Handle manual check-in with loading states
    const handleManualCheckIn = async (checkInData) => {
        setManualCheckInLoading(true);
        
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ghl/checkin/${locationId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    pin: selectedMember.pin,
                    check_in_date: checkInData.date,
                    check_in_time: checkInData.time,
                    device_info: "Manual Check-in"
                })
            });

            if (response.ok) {
                // Close modal and refresh data silently
                setShowManualCheckIn(false);
                setSelectedMember(null);
                loadLocationData(); // Refresh data
                loadTodaysCheckins(locationTimezone); // Refresh today's check-ins with correct timezone
            } else {
                const errorData = await response.json();
                console.error('Check-in failed:', errorData);
                alert('❌ Check-in failed: ' + (errorData.detail || 'Unknown error'));
            }
        } catch (error) {
            console.error('Check-in error:', error);
            alert('❌ Error adding check-in: ' + (error.response?.data?.message || 'Network error'));
        } finally {
            setManualCheckInLoading(false);
        }
    };

    // Apply filters to members list
    const applyFilters = () => {
        const hasFilters = searchTerm || filters.dateFrom || filters.dateTo || filters.beltRank;
        setHasActiveFilters(hasFilters);
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setFilters({ dateFrom: '', dateTo: '', beltRank: '' });
        setHasActiveFilters(false);
    };

    // Get view title
    const getViewTitle = () => {
        switch (activeView) {
            case 'new-members': return '👥 New Members This Month';
            case 'active-members': return '👥 Active Members';
            case 'recent-activity': return '✅ Recent Activity (Last 7 Days)';
            case 'mia-members': return '⚠️ MIA Members (7+ Days)';
            case 'todays-checkins': return '📅 Today\'s Check Ins';
            case 'all-checkins': return '📊 All Check Ins';
            default: return '📊 Report';
        }
    };

    // Render all check-ins table with pagination
    const renderAllCheckInsTable = () => {
        return (
            <div style={{
                backgroundColor: colors.card,
                borderRadius: '12px',
                border: `1px solid ${colors.border}`,
                overflow: 'hidden'
            }}>
                {/* Header with count and pagination info */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    backgroundColor: colors.secondary,
                    borderBottom: `1px solid ${colors.border}`
                }}>
                    <h4 style={{
                        margin: 0,
                        color: colors.text,
                        fontFamily: 'Poppins',
                        fontWeight: '600'
                    }}>
                        Total Check Ins: {checkinPagination.total_count}
                    </h4>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <select
                            value={checkinPagination.limit}
                            onChange={(e) => {
                                const newLimit = parseInt(e.target.value);
                                loadAllCheckins(1, newLimit, checkinFilters.search, checkinFilters.dateFrom, checkinFilters.dateTo);
                            }}
                            style={{
                                padding: '8px',
                                border: `1px solid ${colors.border}`,
                                borderRadius: '6px',
                                fontFamily: 'Poppins',
                                fontSize: '12px'
                            }}
                        >
                            <option value={20}>20 per page</option>
                            <option value={50}>50 per page</option>
                            <option value={100}>100 per page</option>
                        </select>
                    </div>
                </div>

                {/* Table header */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 100px',
                    gap: '16px',
                    padding: '16px',
                    backgroundColor: colors.secondary,
                    fontWeight: '600',
                    color: colors.text,
                    fontFamily: 'Poppins'
                }}>
                    <div>Name</div>
                    <div>Contact</div>
                    <div>Time</div>
                    <div>Type</div>
                </div>
                
                {/* Table rows */}
                {allCheckins.map((checkin, index) => {
                    const timestamp = new Date(checkin.checkin_timestamp);
                    const localTime = formatTimeInTimezone(checkin.checkin_timestamp, locationTimezone);
                    const checkinType = checkin.checkin_type;
                    
                    // Find contact info from main data
                    const contact = data?.contacts?.find(c => c.id === checkin.contact_id);
                    
                    return (
                        <div key={checkin.id || index} style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr 100px',
                            gap: '16px',
                            padding: '16px',
                            borderTop: index > 0 ? `1px solid ${colors.border}` : 'none',
                            fontFamily: 'Poppins'
                        }}>
                            <div>
                                <div 
                                    style={{ 
                                        fontWeight: '600', 
                                        color: colors.text, 
                                        cursor: 'pointer',
                                        textDecoration: 'underline'
                                    }}
                                    title="Click to Open Contact"
                                    onClick={() => window.open(`https://app.thefollowup.ninja/v2/location/${locationId}/contacts/detail/${checkin.contact_id}`, '_blank')}
                                >
                                    {capitalizeName(checkin.contact_name)}
                                </div>
                            </div>
                            <div style={{ color: colors.textLight }}>
                                <div>{contact?.email || 'N/A'}</div>
                                <div>{contact?.phone || 'N/A'}</div>
                            </div>
                            <div style={{ color: colors.text }}>
                                <div>{localTime}</div>
                                <div style={{ fontSize: '12px', color: colors.textLight }}>
                                    {checkin.checkin_date}
                                </div>
                            </div>
                            <div style={{ 
                                color: checkinType === 'Manual' ? colors.warning : colors.success,
                                fontSize: '12px',
                                fontWeight: '500'
                            }}>
                                {checkinType}
                            </div>
                        </div>
                    );
                })}
                
                {allCheckins.length === 0 && (
                    <div style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: colors.textLight,
                        fontFamily: 'Poppins'
                    }}>
                        <div style={{ fontSize: '32px', marginBottom: '16px' }}>📝</div>
                        No check-ins found.
                    </div>
                )}
                
                {/* Pagination controls */}
                {checkinPagination.total_pages > 1 && (
                    <div style={{
                        padding: '16px',
                        borderTop: `1px solid ${colors.border}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: colors.secondary
                    }}>
                        <div style={{ fontSize: '14px', color: colors.textLight }}>
                            Page {checkinPagination.page} of {checkinPagination.total_pages} 
                            ({checkinPagination.total_count} total check-ins)
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={() => {
                                    const newPage = checkinPagination.page - 1;
                                    loadAllCheckins(newPage, checkinPagination.limit, checkinFilters.search, checkinFilters.dateFrom, checkinFilters.dateTo);
                                }}
                                disabled={!checkinPagination.has_prev}
                                style={{
                                    padding: '8px 12px',
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '6px',
                                    backgroundColor: checkinPagination.has_prev ? colors.card : colors.border,
                                    cursor: checkinPagination.has_prev ? 'pointer' : 'not-allowed',
                                    fontFamily: 'Poppins',
                                    fontSize: '12px'
                                }}
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => {
                                    const newPage = checkinPagination.page + 1;
                                    loadAllCheckins(newPage, checkinPagination.limit, checkinFilters.search, checkinFilters.dateFrom, checkinFilters.dateTo);
                                }}
                                disabled={!checkinPagination.has_next}
                                style={{
                                    padding: '8px 12px',
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '6px',
                                    backgroundColor: checkinPagination.has_next ? colors.card : colors.border,
                                    cursor: checkinPagination.has_next ? 'pointer' : 'not-allowed',
                                    fontFamily: 'Poppins',
                                    fontSize: '12px'
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Render check-ins table
    const renderCheckInsTable = (checkins) => {
        return (
            <div style={{
                backgroundColor: colors.card,
                borderRadius: '12px',
                border: `1px solid ${colors.border}`,
                overflow: 'hidden'
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 100px',
                    gap: '16px',
                    padding: '16px',
                    backgroundColor: colors.secondary,
                    fontWeight: '600',
                    color: colors.text,
                    fontFamily: 'Poppins'
                }}>
                    <div>Name</div>
                    <div>Contact</div>
                    <div>Time</div>
                    <div>Type</div>
                </div>
                
                {checkins.map((checkin, index) => {
                    const timestamp = new Date(checkin.checkin_timestamp);
                    const localTime = formatTimeInTimezone(checkin.checkin_timestamp, locationTimezone);
                    const checkinType = checkin.device_info === "Manual Check-in" ? "Manual" : "Kiosk";
                    
                    // Find contact info from main data
                    const contact = data?.contacts?.find(c => c.id === checkin.contact_id);
                    
                    return (
                        <div key={checkin.id || index} style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr 100px',
                            gap: '16px',
                            padding: '16px',
                            borderTop: index > 0 ? `1px solid ${colors.border}` : 'none',
                            fontFamily: 'Poppins'
                        }}>
                            <div>
                                <div 
                                    style={{ 
                                        fontWeight: '600', 
                                        color: colors.text, 
                                        cursor: 'pointer',
                                        textDecoration: 'underline'
                                    }}
                                    title="Click to Open Contact"
                                    onClick={() => window.open(`https://app.thefollowup.ninja/v2/location/${locationId}/contacts/detail/${checkin.contact_id}`, '_blank')}
                                >
                                    {capitalizeName(checkin.contact_name)}
                                </div>
                            </div>
                            <div style={{ color: colors.textLight }}>
                                <div>{contact?.email || 'N/A'}</div>
                                <div>{contact?.phone || 'N/A'}</div>
                            </div>
                            <div style={{ color: colors.text }}>
                                {localTime}
                            </div>
                            <div style={{ 
                                color: checkinType === 'Manual' ? colors.warning : colors.success,
                                fontSize: '12px',
                                fontWeight: '500'
                            }}>
                                {checkinType}
                            </div>
                        </div>
                    );
                })}
                
                {checkins.length === 0 && (
                    <div style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: colors.textLight,
                        fontFamily: 'Poppins'
                    }}>
                        <div style={{ fontSize: '32px', marginBottom: '16px' }}>📝</div>
                        No check-ins found for today.
                    </div>
                )}
            </div>
        );
    };

    // Render member table
    const renderMemberTable = () => {
        const members = getFilteredDataForView(activeView);
        
        return (
            <div style={{
                backgroundColor: colors.card,
                borderRadius: '12px',
                border: `1px solid ${colors.border}`,
                overflow: 'hidden'
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 120px',
                    gap: '16px',
                    padding: '16px',
                    backgroundColor: colors.secondary,
                    fontWeight: '600',
                    color: colors.text,
                    fontFamily: 'Poppins'
                }}>
                    <div>Member</div>
                    <div>Contact</div>
                    <div>Last Check In</div>
                    <div>Actions</div>
                </div>
                
                {members.map((member, index) => {
                    const lastCheckIn = getLastCheckInDate(member);
                    const daysSince = getDaysSinceLastCheckIn(lastCheckIn);
                    const statusText = daysSince === 'Never' ? 'Never' : 
                                     daysSince === 0 ? 'Today' : 
                                     daysSince === 1 ? 'Yesterday' : 
                                     `${daysSince} days ago`;
                    
                    return (
                        <div key={member.id} style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr 120px',
                            gap: '16px',
                            padding: '16px',
                            borderTop: index > 0 ? `1px solid ${colors.border}` : 'none',
                            fontFamily: 'Poppins'
                        }}>
                            <div>
                                <div 
                                    style={{ 
                                        fontWeight: '600', 
                                        color: colors.text, 
                                        cursor: 'pointer',
                                        textDecoration: 'underline'
                                    }}
                                    title="Click to Open Contact"
                                    onClick={() => window.open(`https://app.thefollowup.ninja/v2/location/${locationId}/contacts/detail/${member.id}`, '_blank')}
                                >
                                    {capitalizeName(member.contactName)}
                                </div>
                            </div>
                            <div style={{ color: colors.textLight }}>
                                <div>{member.email}</div>
                                <div>{member.phone}</div>
                            </div>
                            <div 
                                style={{ 
                                    color: daysSince === 'Never' ? colors.danger : daysSince <= 1 ? colors.success : colors.warning,
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                                title="Click to view check-in history"
                                onClick={() => {
                                    setSelectedMemberForHistory(member);
                                    setShowCheckInHistory(true);
                                }}
                            >
                                {statusText}
                            </div>
                            <div>
                                <button
                                    onClick={() => {
                                        setSelectedMember({
                                            ...member,
                                            pin: extractPinFromContact(member)
                                        });
                                        setShowManualCheckIn(true);
                                    }}
                                    style={{
                                        backgroundColor: colors.success,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '6px 12px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Check In
                                </button>
                            </div>
                        </div>
                    );
                })}
                
                {members.length === 0 && (
                    <div style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: colors.textLight,
                        fontFamily: 'Poppins'
                    }}>
                        <div style={{ fontSize: '32px', marginBottom: '16px' }}>📝</div>
                        No members found matching your criteria.
                    </div>
                )}
            </div>
        );
    };

    // Check In URL Modal
    const CheckInUrlModal = () => {
        const checkInUrl = `${window.location.origin}/simple-checkin?location_id=${locationId}`;
        
        return showCheckInUrl ? (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: colors.card,
                    padding: '32px',
                    borderRadius: '16px',
                    maxWidth: '600px',
                    width: '90%'
                }}>
                    <h3 style={{ color: colors.text, marginBottom: '16px', fontFamily: 'Poppins' }}>
                        📱 iPad Check-In Setup
                    </h3>
                    <p style={{ color: colors.textLight, marginBottom: '20px' }}>
                        Copy this URL and open it on your iPad for member check-ins:
                    </p>
                    <div style={{
                        backgroundColor: colors.secondary,
                        padding: '16px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.border}`,
                        marginBottom: '20px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        wordBreak: 'break-all'
                    }}>
                        {checkInUrl}
                    </div>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                            onClick={copyCheckInUrl}
                            style={{
                                backgroundColor: copySuccess ? colors.success : colors.primary,
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 20px',
                                cursor: 'pointer',
                                fontFamily: 'Poppins',
                                fontWeight: '500',
                                transform: copySuccess ? 'scale(1.05)' : 'scale(1)',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {copySuccess ? '✅ Copied!' : '📋 Copy Link'}
                        </button>
                        <button
                            onClick={() => setShowCheckInUrl(false)}
                            style={{
                                backgroundColor: colors.border,
                                color: colors.text,
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 20px',
                                cursor: 'pointer',
                                fontFamily: 'Poppins',
                                fontWeight: '500'
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        ) : null;
    };

    // Manual Check-In Modal
    const ManualCheckInModal = () => {
        const [checkInDate, setCheckInDate] = useState(getCurrentDateInTimezone(locationTimezone));
        const [checkInTime, setCheckInTime] = useState(getCurrentTimeInTimezone(locationTimezone));
        
        // Update check-in date and time when modal opens or timezone changes
        useEffect(() => {
            if (showManualCheckIn && locationTimezone) {
                setCheckInDate(getCurrentDateInTimezone(locationTimezone));
                setCheckInTime(getCurrentTimeInTimezone(locationTimezone));
            }
        }, [showManualCheckIn, locationTimezone]);
        
        if (!showManualCheckIn || !selectedMember) return null;
        
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: colors.card,
                    padding: '32px',
                    borderRadius: '16px',
                    maxWidth: '400px',
                    width: '90%'
                }}>
                    <h3 style={{ color: colors.text, marginBottom: '16px', fontFamily: 'Poppins' }}>
                        ✅ Manual Check-In
                    </h3>
                    <div style={{ marginBottom: '20px' }}>
                        <p style={{ color: colors.text, fontFamily: 'Poppins', marginBottom: '8px' }}>
                            <strong>Member:</strong> {capitalizeName(selectedMember.contactName)}
                        </p>
                        <p style={{ color: colors.textLight, fontFamily: 'Poppins', marginBottom: '16px' }}>
                            <strong>PIN:</strong> {selectedMember.pin}
                        </p>
                        <label style={{ display: 'block', marginBottom: '8px', fontFamily: 'Poppins', fontWeight: '500' }}>
                            Check-In Date ({locationTimezone}):
                        </label>
                        <input
                            type="date"
                            value={checkInDate}
                            onChange={(e) => setCheckInDate(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: `1px solid ${colors.border}`,
                                borderRadius: '8px',
                                fontFamily: 'Poppins',
                                marginBottom: '16px'
                            }}
                        />
                        <label style={{ display: 'block', marginBottom: '8px', fontFamily: 'Poppins', fontWeight: '500' }}>
                            Check-In Time ({locationTimezone}):
                        </label>
                        <input
                            type="time"
                            value={checkInTime}
                            onChange={(e) => setCheckInTime(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: `1px solid ${colors.border}`,
                                borderRadius: '8px',
                                fontFamily: 'Poppins'
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                            onClick={async () => {
                                await handleManualCheckIn({ date: checkInDate, time: checkInTime });
                            }}
                            disabled={manualCheckInLoading}
                            style={{
                                backgroundColor: manualCheckInLoading ? '#cccccc' : colors.success,
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 20px',
                                cursor: manualCheckInLoading ? 'not-allowed' : 'pointer',
                                fontFamily: 'Poppins',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                opacity: manualCheckInLoading ? 0.7 : 1,
                                transform: manualCheckInLoading ? 'scale(0.98)' : 'scale(1)',
                            }}
                        >
                            {manualCheckInLoading ? (
                                <>
                                    <span style={{ marginRight: '8px' }}>⏳</span>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <span style={{ marginRight: '8px' }}>✅</span>
                                    Add Check-In
                                </>
                            )}
                        </button>
                        <button
                            onClick={() => {
                                setShowManualCheckIn(false);
                                setSelectedMember(null);
                            }}
                            style={{
                                backgroundColor: colors.border,
                                color: colors.text,
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 20px',
                                cursor: 'pointer',
                                fontFamily: 'Poppins',
                                fontWeight: '500'
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Early returns for loading and error states
    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: colors.secondary,
                fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                    <h2 style={{ color: colors.text, marginBottom: '8px' }}>Loading Studio Data...</h2>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: colors.secondary,
                fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    textAlign: 'center',
                    backgroundColor: colors.card,
                    padding: '40px',
                    borderRadius: '16px',
                    border: `1px solid ${colors.border}`,
                    maxWidth: '500px'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                    <h2 style={{ color: colors.danger, marginBottom: '8px' }}>Connection Error</h2>
                    <p style={{ color: colors.textLight, marginBottom: '20px' }}>
                        Unable to connect to your studio data: {error}
                    </p>
                    <button
                        onClick={loadLocationData}
                        style={{
                            backgroundColor: colors.primary,
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            cursor: 'pointer',
                            fontFamily: 'Poppins',
                            fontWeight: '500'
                        }}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: colors.secondary,
            fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
            position: 'relative'
        }}>
            {/* Version Indicator */}
            <div style={{
                position: 'fixed',
                bottom: '16px',
                left: '16px',
                backgroundColor: 'rgba(30, 64, 175, 0.9)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'monospace',
                zIndex: 1000
            }}>
                v2.5.2-timezone-aware | {locationTimezone}
            </div>

            {/* Header */}
            <div style={{
                backgroundColor: colors.card,
                borderBottom: `1px solid ${colors.border}`,
                padding: '20px 32px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h1 style={{
                    color: colors.primary,
                    margin: 0,
                    fontSize: '28px',
                    fontWeight: '700'
                }}>
                    🏃 Studio Manager Pro
                </h1>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button
                        onClick={() => setShowCheckInUrl(true)}
                        style={{
                            backgroundColor: colors.primary,
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 16px',
                            cursor: 'pointer',
                            fontFamily: 'Poppins',
                            fontWeight: '500',
                            fontSize: '14px'
                        }}
                    >
                        📱 Check In URL
                    </button>
                </div>
            </div>

            {/* Dashboard Content */}
            {activeView === 'dashboard' && (
                <div style={{ padding: '32px' }}>
                    {/* Stats Cards */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '24px',
                        marginBottom: '32px'
                    }}>
                        <div style={{
                            backgroundColor: colors.card,
                            padding: '24px',
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}`,
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}
                        onClick={() => setActiveView('new-members')}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>👥</div>
                            <h3 style={{ color: colors.text, margin: 0, fontSize: '32px', fontWeight: '700' }}>
                                {getNewMembersThisMonth()}
                            </h3>
                            <p style={{ color: colors.textLight, margin: '4px 0 0 0' }}>New Members This Month</p>
                        </div>

                        <div style={{
                            backgroundColor: colors.card,
                            padding: '24px',
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}`,
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}
                        onClick={() => setActiveView('active-members')}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>💪</div>
                            <h3 style={{ color: colors.text, margin: 0, fontSize: '32px', fontWeight: '700' }}>
                                {getTotalActiveMembers()}
                            </h3>
                            <p style={{ color: colors.textLight, margin: '4px 0 0 0' }}>Total Active Members</p>
                        </div>

                        <div style={{
                            backgroundColor: colors.card,
                            padding: '24px',
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}`,
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}
                        onClick={() => setActiveView('recent-activity')}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
                            <h3 style={{ color: colors.text, margin: 0, fontSize: '32px', fontWeight: '700' }}>
                                {getRecentCheckIns()}
                            </h3>
                            <p style={{ color: colors.textLight, margin: '4px 0 0 0' }}>Last 7 Days</p>
                        </div>

                        <div style={{
                            backgroundColor: colors.card,
                            padding: '24px',
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}`,
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}
                        onClick={() => setActiveView('mia-members')}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚠️</div>
                            <h3 style={{ color: colors.text, margin: 0, fontSize: '32px', fontWeight: '700' }}>
                                {getMIAMembers()}
                            </h3>
                            <p style={{ color: colors.textLight, margin: '4px 0 0 0' }}>MIA Members</p>
                        </div>
                    </div>
                    
                    {/* Today's Check Ins Section */}
                    <div style={{ marginBottom: '32px' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px'
                        }}>
                            <h3 style={{
                                color: colors.text,
                                margin: 0,
                                fontSize: '24px',
                                fontWeight: '600',
                                fontFamily: 'Poppins'
                            }}>
                                📅 Check Ins for {todaysCheckinsDate} ({todaysCheckins.length})
                            </h3>
                            {todaysCheckins.length > 0 && (
                                <button
                                    onClick={() => {
                                        setActiveView('all-checkins');
                                        setSearchTerm('');
                                        setCheckinFilters({ search: '', dateFrom: '', dateTo: '' });
                                        loadAllCheckins(1, 20, '', '', '');
                                    }}
                                    style={{
                                        backgroundColor: 'transparent',
                                        color: colors.primary,
                                        border: `1px solid ${colors.primary}`,
                                        borderRadius: '6px',
                                        padding: '6px 12px',
                                        cursor: 'pointer',
                                        fontFamily: 'Poppins',
                                        fontSize: '12px'
                                    }}
                                >
                                    View All Check Ins
                                </button>
                            )}
                        </div>
                        
                        {/* Show first 5 check-ins */}
                        {renderCheckInsTable(todaysCheckins.slice(0, 5))}
                    </div>
                </div>
            )}

            {/* All Report Views including Today's Check Ins and All Check Ins */}
            {(activeView === 'active-members' || activeView === 'new-members' || activeView === 'recent-activity' || activeView === 'mia-members' || activeView === 'todays-checkins' || activeView === 'all-checkins') && (
                <div style={{ padding: '32px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                        <button
                            onClick={() => setActiveView('dashboard')}
                            style={{
                                backgroundColor: colors.border,
                                color: colors.text,
                                border: 'none',
                                borderRadius: '8px',
                                padding: '8px 16px',
                                cursor: 'pointer',
                                fontFamily: 'Poppins'
                            }}
                        >
                            ← Back to Dashboard
                        </button>
                        <h2 style={{ color: colors.text, margin: 0, fontFamily: 'Poppins' }}>{getViewTitle()}</h2>
                    </div>

                    {/* Search Bar and Filters */}
                    {(activeView === 'all-checkins') ? (
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                placeholder="Search by name, PIN, belt rank..."
                                value={checkinFilters.search}
                                onChange={(e) => setCheckinFilters(prev => ({ ...prev, search: e.target.value }))}
                                style={{
                                    flex: 1,
                                    minWidth: '200px',
                                    padding: '12px',
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '8px',
                                    fontFamily: 'Poppins'
                                }}
                            />
                            
                            <input
                                type="date"
                                placeholder="From Date"
                                value={checkinFilters.dateFrom}
                                onChange={(e) => setCheckinFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                                style={{
                                    padding: '12px',
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '8px',
                                    fontFamily: 'Poppins'
                                }}
                            />
                            <input
                                type="date"
                                placeholder="To Date"
                                value={checkinFilters.dateTo}
                                onChange={(e) => setCheckinFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                                style={{
                                    padding: '12px',
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '8px',
                                    fontFamily: 'Poppins'
                                }}
                            />
                            
                            <button
                                onClick={() => {
                                    loadAllCheckins(1, checkinPagination.limit, checkinFilters.search, checkinFilters.dateFrom, checkinFilters.dateTo);
                                }}
                                style={{
                                    backgroundColor: colors.primary,
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    fontFamily: 'Poppins',
                                    fontWeight: '500'
                                }}
                            >
                                Apply Filters
                            </button>
                            
                            {(checkinFilters.search || checkinFilters.dateFrom || checkinFilters.dateTo) && (
                                <button
                                    onClick={() => {
                                        setCheckinFilters({ search: '', dateFrom: '', dateTo: '' });
                                        loadAllCheckins(1, checkinPagination.limit, '', '', '');
                                    }}
                                    style={{
                                        backgroundColor: colors.danger,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        fontFamily: 'Poppins',
                                        fontWeight: '500'
                                    }}
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    ) : (activeView === 'todays-checkins') ? (
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
                            <input
                                type="text"
                                placeholder="Search today's check-ins..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '8px',
                                    fontFamily: 'Poppins'
                                }}
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    style={{
                                        backgroundColor: colors.danger,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        fontFamily: 'Poppins',
                                        fontWeight: '500'
                                    }}
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    ) : (
                        /* Regular member search bar */
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
                            <input
                                type="text"
                                placeholder="Search by name, email, or phone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '8px',
                                    fontFamily: 'Poppins'
                                }}
                            />
                            {searchTerm && (
                                <button
                                    onClick={clearFilters}
                                    style={{
                                        backgroundColor: colors.danger,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        fontFamily: 'Poppins',
                                        fontWeight: '500'
                                    }}
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    )}

                    {/* Content */}
                    {activeView === 'all-checkins' ? (
                        renderAllCheckInsTable()
                    ) : activeView === 'todays-checkins' ? (
                        renderCheckInsTable(
                            todaysCheckins.filter(checkin => {
                                if (!searchTerm) return true;
                                const name = checkin.contact_name || '';
                                const pin = checkin.pin_used || '';
                                const rank = checkin.belt_rank || '';
                                return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                       pin.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                       rank.toLowerCase().includes(searchTerm.toLowerCase());
                            })
                        )
                    ) : (
                        /* Members Table */
                        renderMemberTable()
                    )}
                </div>
            )}
            
            {/* Modals */}
            <CheckInUrlModal />
            <ManualCheckInModal />
            {showCheckInHistory && (
                <CheckInHistoryModal
                    member={selectedMemberForHistory}
                    locationId={locationId}
                    onClose={() => {
                        setShowCheckInHistory(false);
                        setSelectedMemberForHistory(null);
                    }}
                    colors={colors}
                />
            )}
        </div>
    );
};

export default TFUNDashboard;